<template>
  <EquipmentBase
    v-if="!hideGasPropertiesForHpsLiquidType"
    :section-id="ScenarioDetailSectionKey.GAS_PROPERTIES"
  >
    <EquipmentHeader>
      <template #title>
        <div
          class="flex justify-content-between align-items-center flex-wrap pb-2"
        >
          Gas Properties at Standard Conditions
        </div>
      </template>
    </EquipmentHeader>
    <div class="field-grid">
      <ConverterInput
        v-if="!specifyGasPercentages"
        :rule-names="['gas_specific_gravity_zero']"
        label="Gas Gravity"
        :disabled="specifyGasPercentages"
        :base-unit="DensityUnits.SpecificGravity"
        :decimal-places="3"
        :model="
          DensityUnits.options([
            DensityUnits.API,
            DensityUnits.KilogramsPerCubicMeter,
            DensityUnits.PoundsPerCubicFoot
          ])
        "
        mode="write"
        :model-value="wellProperties.gas_specific_gravity"
        @update:model-value="
          wellPropertiesForm.update({ gas_specific_gravity: $event })
        "
      />
      <ConverterInput
        v-if="specifyGasPercentages"
        :rule-names="['gas_specific_gravity_zero']"
        label="Gas Gravity"
        :disabled="specifyGasPercentages"
        :base-unit="DensityUnits.SpecificGravity"
        :decimal-places="3"
        :model="
          DensityUnits.options([
            DensityUnits.API,
            DensityUnits.KilogramsPerCubicMeter,
            DensityUnits.PoundsPerCubicFoot
          ])
        "
        mode="convert"
        :model-value="calculatedGasSpecificGravity.value"
      />
      <ConverterInput
        label="% H2S"
        title="Hydrogen Sulfide"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.h2s_percent"
        @update:model-value="wellPropertiesForm.update({ h2s_percent: $event })"
      />
      <ConverterInput
        label="% CO2"
        title="Carbon Dioxide"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.co2_percent"
        @update:model-value="wellPropertiesForm.update({ co2_percent: $event })"
      />
      <ConverterInput
        label="% N2"
        title="Nitrogen"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.n2_percent"
        @update:model-value="wellPropertiesForm.update({ n2_percent: $event })"
      />
    </div>

    <Divider>Gas Type</Divider>

    <div
      v-if="isEspOnShore || !(isCondensateLiquidType || isOilLiquidType)"
      class="flex align-items-center gap-2 p-2 flex-wrap"
    >
      <Checkbox
        input-id="wet-gas"
        :binary="true"
        :disabled="isCondensateLiquidType || isOilLiquidType"
        :model-value="selectedGasType === Well_Gas_Type_Options_Enum.Wet"
        @update:model-value="onGasTypeToggle($event)"
      />
      <label for="wet-gas">Wet Gas</label>
    </div>

    <div class="field-grid">
      <ConverterInput
        v-if="!isEspOnShore || isWetGas"
        label="Condensate API Override"
        :base-unit="DensityUnits.API"
        :model="
          DensityUnits.options([
            DensityUnits.KilogramsPerCubicMeter,
            DensityUnits.GasPoundsPerCubicFoot
          ])
        "
        :nullable="true"
        mode="write"
        :model-value="wellProperties.condensate_api_override"
        @update:model-value="
          wellPropertiesForm.update({ condensate_api_override: $event })
        "
      />
      <ConverterInput
        v-if="isEspOnShore ? isWetGas && specifyGasPercentages : true"
        label="Condensate API"
        :model-value="selectedCondensateApi"
        :base-unit="DensityUnits.API"
        :model="DensityUnits.options()"
        mode="convert"
      />
      <ConverterInput
        v-if="!isEspOnShore || isWetGas"
        label="Dew Point Pressure Override"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        :nullable="true"
        mode="write"
        :model-value="wellProperties.dew_point_pressure_psia_override"
        @update:model-value="
          wellPropertiesForm.update({
            dew_point_pressure_psia_override: $event
          })
        "
      />
      <ConverterInput
        v-if="!isEspOnShore || isWetGas"
        label="Dew Point Pressure"
        :model-value="scenarioCalculations.selectedDewPointPressurePSIA.value"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        mode="convert"
      />
    </div>

    <div class="flex gap-2 align-items-center p-2">
      <InputSwitch v-model="specifyGasPercentages" />
      Specify Gas Percentages
    </div>
    <Divider v-if="specifyGasPercentages"
      >Total: {{ totalGasPercentages }} %</Divider
    >
    <Message
      v-if="specifyGasPercentages && !isGasPercentagesTotalValid"
      severity="warn"
      :closable="false"
      >Gas percentages should total 100% — calculated values (gas specific
      gravity, GCR, condensate rate, etc.) assume a complete
      composition.</Message
    >
    <div v-if="specifyGasPercentages" class="field-grid">
      <ConverterInput
        label="% C1"
        title="Methane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.c1_methane_percent"
        @update:model-value="
          wellPropertiesForm.update({ c1_methane_percent: $event })
        "
      />
      <ConverterInput
        label="% C2"
        title="Ethane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.c2_ethane_percent"
        @update:model-value="
          wellPropertiesForm.update({ c2_ethane_percent: $event })
        "
      />
      <ConverterInput
        label="% C3"
        title="Propane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.c3_propane_percent"
        @update:model-value="
          wellPropertiesForm.update({ c3_propane_percent: $event })
        "
      />
      <ConverterInput
        label="% i-C4"
        title="Isobutane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.ic4_isobutene_percent"
        @update:model-value="
          wellPropertiesForm.update({ ic4_isobutene_percent: $event })
        "
      />
      <ConverterInput
        label="% n-C4"
        title="Butane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.nc4_butane_percent"
        @update:model-value="
          wellPropertiesForm.update({ nc4_butane_percent: $event })
        "
      />
      <ConverterInput
        label="% i-C5"
        title="Isopentane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.ic5_isopentane_percent"
        @update:model-value="
          wellPropertiesForm.update({ ic5_isopentane_percent: $event })
        "
      />
      <ConverterInput
        label="% n-C5"
        title="Pentane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.nc5_bentane_percent"
        @update:model-value="
          wellPropertiesForm.update({ nc5_bentane_percent: $event })
        "
      />
      <ConverterInput
        label="% C6"
        title="Hexane"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.c6_hexane_percent"
        @update:model-value="
          wellPropertiesForm.update({ c6_hexane_percent: $event })
        "
      />
      <ConverterInput
        label="% C7+"
        title="Heptane +"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.c7_heptane_percent"
        @update:model-value="
          wellPropertiesForm.update({ c7_heptane_percent: $event })
        "
      />
      <ConverterInput
        label="% O2"
        title="Oxygen"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.o2_percent"
        @update:model-value="wellPropertiesForm.update({ o2_percent: $event })"
      />
      <ConverterInput
        label="% Ar"
        title="Argon"
        :base-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :model-value="wellProperties.ar_percent"
        @update:model-value="wellPropertiesForm.update({ ar_percent: $event })"
      />
    </div>
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
// import ViscosityTests from "@/components/ViscosityTests.vue";
import Message from "primevue/message";
import Checkbox from "primevue/checkbox";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { DensityUnits } from "@/libs/units/density";
import { PercentageUnits } from "@/libs/units/percentage";
import { injectStrict } from "@/utils/utils";
import {
  scenarioCalculationsKey,
  sizingFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import {
  Hps_Liquid_Type_Enum,
  Sizing_Pumping_Type_Enum,
  Well_Gas_Type_Options_Enum
} from "gql/api";
import { PressureUnits } from "@/libs/units/pressure";

defineEmits(["update:well-properties", "hover"]);

const sizingForm = injectStrict(sizingFormKey, null);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellProperties = computed(() => wellPropertiesForm.data.value);
const specifyGasPercentages = ref(!wellProperties.value.gas_specific_gravity);
const scenarioCalculations = injectStrict(scenarioCalculationsKey);

const isEspOnShore = computed(
  () =>
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
);

const calculatedGasSpecificGravity = computed(() => {
  return scenarioCalculations.fluidProperties.gasSpecificGravity;
});

const hideGasPropertiesForHpsLiquidType = computed(() => {
  const type = wellProperties.value.hps_liquid_type;
  return (
    type === Hps_Liquid_Type_Enum.Water || type === Hps_Liquid_Type_Enum.Amine
  );
});

const isCondensateLiquidType = computed(
  () => wellProperties.value.hps_liquid_type === Hps_Liquid_Type_Enum.Condensate
);

const isOilLiquidType = computed(
  () => wellProperties.value.hps_liquid_type === Hps_Liquid_Type_Enum.Oil
);

const selectedGasType = computed(
  () => wellProperties.value.gas_type ?? Well_Gas_Type_Options_Enum.Dry
);

const isWetGas = computed(
  () => selectedGasType.value === Well_Gas_Type_Options_Enum.Wet
);

const updateGasType = (value: Well_Gas_Type_Options_Enum) => {
  if (isCondensateLiquidType.value) {
    wellPropertiesForm.update({ gas_type: Well_Gas_Type_Options_Enum.Wet });
    return;
  }

  if (isOilLiquidType.value) {
    wellPropertiesForm.update({ gas_type: Well_Gas_Type_Options_Enum.Dry });
    return;
  }

  wellPropertiesForm.update({ gas_type: value });
};

const onGasTypeToggle = (isWet: boolean) => {
  if (isCondensateLiquidType.value) {
    updateGasType(Well_Gas_Type_Options_Enum.Wet);
    return;
  }

  if (isOilLiquidType.value) {
    updateGasType(Well_Gas_Type_Options_Enum.Dry);
    return;
  }

  updateGasType(
    isWet ? Well_Gas_Type_Options_Enum.Wet : Well_Gas_Type_Options_Enum.Dry
  );
};

// PVT!O64 — already override-aware, so this is the resolved value.
const selectedCondensateApi = computed(() => {
  return scenarioCalculations.fluidProperties.fluidPropertiesAtReservoir.value
    .condensateApi;
});

const totalGasPercentages = computed(() => {
  return parseFloat(
    (
      wellProperties.value.c1_methane_percent +
      wellProperties.value.c2_ethane_percent +
      wellProperties.value.c3_propane_percent +
      wellProperties.value.nc4_butane_percent +
      wellProperties.value.ic4_isobutene_percent +
      wellProperties.value.ic5_isopentane_percent +
      wellProperties.value.nc5_bentane_percent +
      wellProperties.value.c6_hexane_percent +
      wellProperties.value.c7_heptane_percent +
      wellProperties.value.co2_percent +
      wellProperties.value.n2_percent +
      wellProperties.value.h2s_percent +
      (wellProperties.value.o2_percent || 0) +
      (wellProperties.value.ar_percent || 0)
    ).toFixed(4)
  );
});

// Downstream calcs (gas SG, GCR, condensate rate, etc.) assume the entered
// percentages sum to 100 — they aren't normalised by the actual total, so an
// incomplete composition silently produces a nonsense gas SG rather than an
// error. This only warns; it doesn't block calculation.
const isGasPercentagesTotalValid = computed(
  () => Math.abs(totalGasPercentages.value - 100) < 0.01
);

const initialGasSpecificGravity = ref();
watch(
  () => wellProperties.value.hps_liquid_type,
  liquidType => {
    if (liquidType === Hps_Liquid_Type_Enum.Oil) {
      wellPropertiesForm.update({ gas_type: Well_Gas_Type_Options_Enum.Dry });
      return;
    }

    if (liquidType === Hps_Liquid_Type_Enum.Condensate) {
      wellPropertiesForm.update({ gas_type: Well_Gas_Type_Options_Enum.Wet });
    }
  },
  { immediate: true }
);

watch(specifyGasPercentages, () => {
  if (specifyGasPercentages.value === true) {
    initialGasSpecificGravity.value =
      wellPropertiesForm.data.value.gas_specific_gravity;
    wellPropertiesForm.update({ gas_specific_gravity: 0 });
  } else {
    wellPropertiesForm.update({
      gas_specific_gravity: initialGasSpecificGravity.value
    });
  }
});
</script>

<style scoped>
:deep(.field-grid) {
  row-gap: 0.75rem;
}
</style>
